import React, { useEffect, useState } from "react";
import "./styles.scss";
import { setDateRange, useDateRangeSelector } from "../../redux/actions/app";
import type DateValueI from "../../model/DateValueI";
import DatePicker from "react-datepicker";
import { useDispatch } from "react-redux";
import KC_Button, { KC_ButtonSize } from "../KC_Button/KC_Button";
import moment from "moment-timezone";
import { faRefresh } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { tt } from "../../utils/i18n/i18n";

type DateRangePreset =
  | "this_month"
  | "last_month"
  | "this_week"
  | "last_week"
  | "free";

interface PropsI {
  offsetRight?: boolean;
  label?: string;
  value?: DateValueI;
  showWeekChangers?: boolean;
  className?: string;
  hideDate?: boolean;
  hide?: boolean;
}

export const KC_DateRangePicker = (props: PropsI) => {
  const dispatch = useDispatch();
  const dateRange = useDateRangeSelector();

  const [activePreset, setActivePreset] =
    useState<DateRangePreset>("this_month");

  const [startDate, setStartDate] = useState<Date>(dateRange.start);
  const [endDate, setEndDate] = useState<Date>(dateRange.end);

  useEffect(() => {
    if (dateRange.start && dateRange.end) {
      setStartDate(dateRange.start);
      setEndDate(dateRange.end);
    }
  }, [dateRange]);

  const updateDateRange = (newDateRange: DateValueI) => {
    dispatch(setDateRange(newDateRange));
  };

  const navigatePrevious = () => {
    switch (activePreset) {
      case "this_week":
      case "last_week":
        updateDateRange({
          start: moment(startDate)
            .subtract(1, "week")
            .startOf("isoWeek")
            .toDate(),
          end: moment(endDate).subtract(1, "week").endOf("isoWeek").toDate()
        });
        break;
      case "this_month":
      case "last_month":
        updateDateRange({
          start: moment(startDate)
            .subtract(1, "month")
            .startOf("month")
            .toDate(),
          end: moment(endDate).subtract(1, "month").endOf("month").toDate()
        });
        break;
      case "free":
        const daysDiff = moment(endDate).diff(moment(startDate), "days");
        updateDateRange({
          start: moment(startDate)
            .subtract(daysDiff + 1, "days")
            .toDate(),
          end: moment(endDate)
            .subtract(daysDiff + 1, "days")
            .toDate()
        });
        break;
    }
  };

  const navigateNext = () => {
    switch (activePreset) {
      case "this_week":
        updateDateRange({
          start: moment(startDate).add(1, "week").startOf("isoWeek").toDate(),
          end: moment(endDate).add(1, "week").endOf("isoWeek").toDate()
        });
        break;
      case "last_week":
        updateDateRange({
          start: moment(startDate).startOf("isoWeek").toDate(),
          end: moment(endDate).endOf("isoWeek").toDate()
        });
        break;
      case "this_month":
        updateDateRange({
          start: moment(startDate).add(1, "month").startOf("month").toDate(),
          end: moment(endDate).add(1, "month").endOf("month").toDate()
        });
        break;
      case "last_month":
        updateDateRange({
          start: moment(startDate).startOf("month").toDate(),
          end: moment(endDate).endOf("month").toDate()
        });
        break;
      case "free":
        const daysDiff = moment(endDate).diff(moment(startDate), "days");
        updateDateRange({
          start: moment(startDate)
            .add(daysDiff + 1, "days")
            .toDate(),
          end: moment(endDate)
            .add(daysDiff + 1, "days")
            .toDate()
        });
        break;
    }
  };

  const setDateRangePreset = (preset: DateRangePreset) => {
    setActivePreset(preset);

    let newStart: Date;
    let newEnd: Date;

    switch (preset) {
      case "this_month":
        newStart = moment().startOf("month").toDate();
        newEnd = moment().endOf("month").toDate();
        setActivePreset("this_month");
        break;
      case "last_month":
        newStart = moment().subtract(1, "month").startOf("month").toDate();
        newEnd = moment().subtract(1, "month").endOf("month").toDate();
        setActivePreset("last_month");
        break;
      case "this_week":
        newStart = moment().startOf("isoWeek").toDate();
        newEnd = moment().endOf("isoWeek").toDate();
        setActivePreset("this_week");
        break;
      case "last_week":
        newStart = moment().subtract(1, "week").startOf("isoWeek").toDate();
        newEnd = moment().subtract(1, "week").endOf("isoWeek").toDate();
        setActivePreset("last_week");
        break;
      case "free":
        setActivePreset("free");
        return; // Don't change dates for free mode
    }

    updateDateRange({ start: newStart, end: newEnd });
  };

  return (
    <div className="d-flex flex-column flex-sm-row align-items-center gap-sm kc-date-range-picker">
      <div className="d-flex flex-row align-items-center gap-sm">
        <KC_Button
          text={<>&larr;</>}
          type="button"
          className="d-flex border align-items-center justify-content-center"
          size={KC_ButtonSize.Medium}
          isPrimary={false}
          isInverted={true}
          isSecondary={false}
          onClick={navigatePrevious}
        />
        <DatePicker
          onChange={(sd) => {
            const newStart = sd as Date;
            setStartDate(newStart);
            setActivePreset("free");
          }}
          locale="fr"
          dateFormat="P"
          selected={startDate}
          id="date-picker-sd"
          enableTabLoop={false}
          onKeyDown={(e) => {
            e.preventDefault();
          }}
        />
        <DatePicker
          onChange={(ed) => {
            const newEnd = ed as Date;
            setEndDate(newEnd);
            setActivePreset("free");
          }}
          locale="fr"
          dateFormat="P"
          selected={endDate}
          enableTabLoop={false}
          id="date-picker-ed"
          onKeyDown={(e) => {
            e.preventDefault();
          }}
        />
        <KC_Button
          text={<>&rarr;</>}
          type="button"
          className="d-flex border align-items-center justify-content-center"
          size={KC_ButtonSize.Medium}
          isPrimary={false}
          isInverted={true}
          isSecondary={false}
          onClick={navigateNext}
        />
        <KC_Button
          text={tt("display_dates")}
          type="button"
          className="d-none d-sm-flex border"
          size={KC_ButtonSize.Medium}
          isPrimary={false}
          isInverted={true}
          isSecondary={false}
          onClick={() => {
            updateDateRange({ start: startDate, end: endDate });
          }}
        />
        <KC_Button
          text={<FontAwesomeIcon icon={faRefresh} />}
          type="button"
          className="d-block d-sm-none"
          size={KC_ButtonSize.Medium}
          isPrimary={true}
          isInverted={false}
          isSecondary={false}
          onClick={() => {
            updateDateRange({ start: dateRange.start, end: dateRange.end });
          }}
        />
      </div>
    </div>
  );
};
